class CollectionTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.collection-tabs__tab');
    this.panels = this.querySelectorAll('.collection-tabs__panel');
    this.dynamicCta = this.hasAttribute('data-dynamic-cta');
    this.ctas = this.dynamicCta ? this.querySelectorAll('.collection-tabs__cta') : null;
    this.bindEvents();
    this.initNavigation();
    requestAnimationFrame(() => this.syncFeaturedHeights());
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });
    window.addEventListener('resize', () => {
      this.syncFeaturedHeights();
      this.updateNavVisibility();
    });
  }

  initNavigation() {
    this.panels.forEach((panel) => {
      const swiperEl = panel.querySelector('custom-swiper');
      const prevBtn = panel.querySelector('.collection-tabs__nav--prev');
      const nextBtn = panel.querySelector('.collection-tabs__nav--next');

      if (!swiperEl || !prevBtn || !nextBtn) return;

      const setup = () => {
        const swiper = swiperEl.swiper;
        if (!swiper) return;

        prevBtn.addEventListener('click', () => swiper.slidePrev());
        nextBtn.addEventListener('click', () => swiper.slideNext());

        swiper.on('slideChange', () => this.updateNavState(swiper, prevBtn, nextBtn));
        swiper.on('reachEnd', () => this.updateNavState(swiper, prevBtn, nextBtn));
        swiper.on('reachBeginning', () => this.updateNavState(swiper, prevBtn, nextBtn));
        swiper.on('fromEdge', () => this.updateNavState(swiper, prevBtn, nextBtn));
        swiper.on('progress', () => this.updateNavState(swiper, prevBtn, nextBtn));
        swiper.on('resize', () => this.updateNavState(swiper, prevBtn, nextBtn));

        this.updateNavState(swiper, prevBtn, nextBtn);
      };

      if (swiperEl.swiper) {
        setup();
      } else {
        const observer = new MutationObserver(() => {
          if (swiperEl.swiper) {
            observer.disconnect();
            setup();
          }
        });
        observer.observe(swiperEl, { childList: true, subtree: true });
        setTimeout(() => { observer.disconnect(); setup(); }, 500);
      }
    });
  }

  updateNavState(swiper, prevBtn, nextBtn) {
    const isLocked = swiper.isLocked;
    const isBeginning = swiper.isBeginning || swiper.progress <= 0;
    const isEnd = swiper.isEnd || swiper.progress >= 1;

    prevBtn.style.display = isBeginning || isLocked ? 'none' : '';
    nextBtn.style.display = isEnd || isLocked ? 'none' : '';
  }

  updateNavVisibility() {
    this.panels.forEach((panel) => {
      const swiperEl = panel.querySelector('custom-swiper');
      const prevBtn = panel.querySelector('.collection-tabs__nav--prev');
      const nextBtn = panel.querySelector('.collection-tabs__nav--next');
      if (!swiperEl?.swiper || !prevBtn || !nextBtn) return;
      this.updateNavState(swiperEl.swiper, prevBtn, nextBtn);
    });
  }

  switchTab(activeTab) {
    const index = activeTab.dataset.tabIndex;

    this.tabs.forEach((tab) => {
      tab.classList.toggle('collection-tabs__tab--active', tab.dataset.tabIndex === index);
    });

    if (this.dynamicCta && this.ctas?.length) {
      const url = activeTab.dataset.collectionUrl;
      if (url) {
        this.ctas.forEach((cta) => { cta.href = url; });
      }
    }

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === index;
      panel.classList.toggle('collection-tabs__panel--active', isActive);

      if (isActive) {
        const swiperEl = panel.querySelector('custom-swiper');
        if (swiperEl?.swiper) {
          swiperEl.swiper.update();
          const prevBtn = panel.querySelector('.collection-tabs__nav--prev');
          const nextBtn = panel.querySelector('.collection-tabs__nav--next');
          if (prevBtn && nextBtn) {
            this.updateNavState(swiperEl.swiper, prevBtn, nextBtn);
          }
        }
      }
    });

    this.syncFeaturedHeights();
  }

  syncFeaturedHeights() {
    this.panels.forEach((panel) => {
      const featured = panel.querySelector('.collection-tabs__slide--featured');
      if (!featured) return;

      featured.style.height = '';
      const cards = panel.querySelectorAll('.product-card-custom');
      if (!cards.length) return;

      let maxHeight = 0;
      cards.forEach((card) => {
        const h = card.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      if (maxHeight > 0) {
        featured.style.height = maxHeight + 'px';
      }
    });
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
